<template>
  <div class="ccma-vat-liability">
    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p class="loading-text">{{ t('consultantCorner.vatLiability.LoadingVatInformation') }}</p>
      </div>
    </div>

    <!-- Error notification -->
    <div v-if="saveError" class="save-error-notification">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-details">
          <h3>{{ t('consultantCorner.vatLiability.SaveError') }}</h3>
          <p>{{ saveErrorMessage }}</p>
        </div>
        <button class="error-close" @click="clearSaveError">×</button>
      </div>
      <div class="error-actions">
        <button class="baseBtn primaryBtn" @click="saveVatInfo">{{ t('consultantCorner.vatLiability.TryAgain') }}</button>
      </div>
    </div>

    <!-- Success notification -->
    <div v-if="saveSuccess" class="save-success-notification">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <div class="success-message">
          <p>{{ t('consultantCorner.vatLiability.SaveSuccess') }}</p>
        </div>
        <button class="success-close" @click="clearSaveSuccess">×</button>
      </div>
    </div>

    <div v-if="!isLoading">
      <!-- Header Section -->
      <div class="vat-header">
        <div class="first">
          <div class="vat-title">{{ t('consultantCorner.vatLiability.VATLiability') }}</div>
          <div class="vat-subtitle mb-1">{{ t('consultantCorner.vatLiability.VATLiabilityDescription') }}</div>
        </div>
        <div class="vat-buttons">
          <button
            class="baseBtn primaryBtn"
            type="button"
            @click="saveVatInfo"
            :disabled="isSaveDisabled"
          >
            {{ isSaving ? t('consultantCorner.vatLiability.Saving') : t('consultantCorner.vatLiability.Save') }}
          </button>
        </div>
      </div>

      <!-- VAT Information Form -->
      <div class="vat-form-container">
        <div class="vat-form-content">
          <form @submit.prevent="saveVatInfo">
            <fieldset class="k-form-fieldset">

              <!-- VAT Information Section -->
              <div class="form-section">
                <h3 class="section-title">{{ t('consultantCorner.vatLiability.VATInformation') }}</h3>

                <div class="form-row">
                  <div class="form-field">
                    <label class="form-label required">{{ t('consultantCorner.vatLiability.VATIDType') }}</label>
                    <DropDownList
                      :data-items="vatIdTypes"
                      v-model="formData.vatIdType"
                      text-field="text"
                      value-field="value"
                      class="form-input"
                      :disabled="hasExistingVatData"
                    />
                    <div v-if="hasExistingVatData" class="help-text">
                      {{ t('consultantCorner.vatLiability.VATCannotBeModified') }}
                    </div>
                  </div>

                  <div class="form-field">
                    <label class="form-label">
                      {{ formData.vatIdType.value === 'VAT' ? t('consultantCorner.vatLiability.VATID') : t('consultantCorner.vatLiability.TaxID') }}
                      <span v-if="!isVatIdDisabled"> *</span>
                    </label>
                    <input
                      v-model="formData.vatId"
                      type="text"
                      class="form-input"
                      :class="{ 'error': errors.vatId }"
                      :placeholder="t('consultantCorner.vatLiability.VATIDPlaceholder')"
                      :disabled="isVatIdDisabled"
                    />
                    <div v-if="errors.vatId" class="error-message">{{ errors.vatId }}</div>
                    <div v-if="formData.vatId && !errors.vatId && isValidFormat && !isVatIdDisabled" class="help-text">
                      {{ t('consultantCorner.vatLiability.ValidFormat') }}
                    </div>
                    <div v-if="formData.vatIdType.value === 'TAXID'" class="help-text">
                      {{ t('consultantCorner.vatLiability.TaxIdNoInputRequired') }}
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { PwsTaxExemptGet, TaxExemptNUWCreate } from '../../api/Business'
import { GetBeeNumberAuthenicatedUser, GetCountryCode } from '../../stores/SettingsStore'

const { t } = useI18n()
const logger = useLogger()

// State management
const isLoading = ref(false)
const isSaving = ref(false)
const saveError = ref(false)
const saveSuccess = ref(false)
const saveErrorMessage = ref('')
const countryCode = ref('')

const formData = ref({
  vatIdType: { value: 'VAT', labelKey: 'labels.options.VAT', text: t('labels.options.VAT') },
  vatId: ''
})

const originalData = ref({ ...formData.value })
const errors = ref<Record<string, string>>({})
const hasExistingVatData = ref(false)
const originalVatType = ref<string>('VAT')

const vatIdTypes = [
  { value: 'VAT', labelKey: 'labels.options.VAT', text: t('labels.options.VAT') },
  { value: 'TAXID', labelKey: 'labels.options.TAXID', text: t('labels.options.TAXID') }
]

// Determine if VAT ID field should be disabled
const isVatIdDisabled = computed(() => {
  // Disable if user has existing VAT data (can't modify VAT once set)
  if (hasExistingVatData.value) {
    return true
  }
  // Disable if TAXID is selected
  if (formData.value.vatIdType.value === 'TAXID') {
    return true
  }
  return false
})

// Determine if Save button should be disabled
const isSaveDisabled = computed(() => {
  // Disable if currently saving
  if (isSaving.value) {
    return true
  }
  // Disable if TAXID is selected
  if (formData.value.vatIdType.value === 'TAXID') {
    return true
  }
  // Disable if user has existing VAT data and hasn't changed anything
  if (hasExistingVatData.value) {
    return true
  }
  return false
})

// Validation for VAT/Tax ID format based on country
const isValidFormat = computed(() => {
  const vatId = formData.value.vatId.trim()
  if (!vatId) return false

  if (formData.value.vatIdType.value === 'VAT') {
    // VAT validation based on country code
    switch (countryCode.value) {
      case 'DE': // German VAT: DE + 9 digits
        return /^DE\d{9}$/.test(vatId)
      case 'AT': // Austrian VAT: ATU + 8 digits
        return /^ATU\d{8}$/.test(vatId)
      case 'FR': // French VAT: FR + 11 characters (2 letters + 9 digits)
        return /^FR[A-Z0-9]{2}\d{9}$/.test(vatId)
      case 'US': // US doesn't use VAT, but if entered, basic format
        return /^[A-Z0-9]{5,15}$/.test(vatId)
      default:
        // Generic VAT format for other countries
        return /^[A-Z]{2}[A-Z0-9]{5,15}$/.test(vatId)
    }
  } else {
    // Tax ID validation based on country code
    switch (countryCode.value) {
      case 'US': // US SSN or EIN format
        return /^(\d{3}-\d{2}-\d{4}|\d{2}-\d{7})$/.test(vatId) || /^\d{9}$/.test(vatId)
      case 'DE': // German Tax ID: 11 digits
        return /^\d{11}$/.test(vatId)
      case 'FR': // French Tax ID (SIREN): 9 digits
        return /^\d{9}$/.test(vatId)
      case 'AT': // Austrian Tax ID: 9 digits
        return /^\d{9}$/.test(vatId)
      default:
        // Generic Tax ID - at least 5 characters
        return vatId.length >= 5
    }
  }
})

// Load existing VAT information on mount
onMounted(async () => {
  // Load country code first
  try {
    countryCode.value = await GetCountryCode()
    logger.info('Country code loaded:', countryCode.value)
  } catch (error) {
    logger.error('Error loading country code:', error)
    countryCode.value = 'US' // Default fallback
  }

  await loadVatInformation()
})

const loadVatInformation = async () => {
  try {
    isLoading.value = true
    const beeNumber = await GetBeeNumberAuthenicatedUser()

    if (!beeNumber) {
      logger.warn('No bee number found for authenticated user')
      return
    }

    const res = await PwsTaxExemptGet(beeNumber.toString())
    const response = res.item

    if (response && response.taxExemptCode) {
      // Get the raw value from API response
      const apiVatType = response.codeIdExtendedTaxId?.codeId || 'VAT'

      // Find the matching object from vatIdTypes array
      const matchingVatType = vatIdTypes.find(item => item.value === apiVatType) || vatIdTypes[0]

      formData.value = {
        vatIdType: matchingVatType, // Assign the full object, not just the value
        vatId: response.taxExemptCode || ''
      }
      originalData.value = { ...formData.value }

      // Track if user has existing VAT data (not TAXID)
      hasExistingVatData.value = apiVatType === 'VAT'
      originalVatType.value = apiVatType

      logger.info('VAT form data set:', formData.value)
      logger.info('Has existing VAT data:', hasExistingVatData.value)
    }
  } catch (error) {
    logger.error('Error loading VAT information:', error)
    // Don't show error for loading - just log it
  } finally {
    isLoading.value = false
  }
}

// Watch for vatIdType changes to clear vatId when switching to TAXID
watch(() => formData.value.vatIdType, (newType) => {
  if (newType.value === 'TAXID') {
    formData.value.vatId = ''
    errors.value = {}
  }
})

const validateVatInfo = (): boolean => {
  errors.value = {}

  // Skip validation if TAXID is selected
  if (formData.value.vatIdType.value === 'TAXID') {
    return false
  }

  if (!formData.value.vatId.trim()) {
    errors.value.vatId = t('consultantCorner.vatLiability.RequiredField')
    return false
  }

  if (!isValidFormat.value) {
    if (formData.value.vatIdType.value === 'VAT') {
      // Country-specific VAT format error messages
      switch (countryCode.value) {
        case 'DE':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidVATFormatDE')
          break
        case 'AT':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidVATFormatAT')
          break
        case 'FR':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidVATFormatFR')
          break
        case 'US':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidVATFormatUS')
          break
        default:
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidVATFormat')
      }
    } else {
      // Country-specific Tax ID format error messages
      switch (countryCode.value) {
        case 'US':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidTaxIDFormatUS')
          break
        case 'DE':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidTaxIDFormatDE')
          break
        case 'FR':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidTaxIDFormatFR')
          break
        case 'AT':
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidTaxIDFormatAT')
          break
        default:
          errors.value.vatId = t('consultantCorner.vatLiability.InvalidTaxIDFormat')
      }
    }
    return false
  }

  return true
}

const saveVatInfo = async () => {
  if (!validateVatInfo()) {
    return
  }

  try {
    isSaving.value = true
    saveError.value = false
    saveSuccess.value = false

    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (!beeNumber) {
      throw new Error(t('errors.userNotAuthenticatedOrInvalidBeeNumber'))
    }

    const requestData = {
      BeeNumber: beeNumber.toString(),
      TaxExemptCode: formData.value.vatId,
      CodeIdExtendedTaxId: formData.value.vatIdType.value
    }

    await TaxExemptNUWCreate(requestData)

    originalData.value = { ...formData.value }
    saveSuccess.value = true

    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      saveSuccess.value = false
    }, 3000)

    logger.info('VAT information saved successfully')
  } catch (error) {
    logger.error('Error saving VAT information:', error)
    saveError.value = true
    saveErrorMessage.value = error instanceof Error ? error.message : t('consultantCorner.vatLiability.UnknownError')
  } finally {
    isSaving.value = false
  }
}

const clearSaveError = () => {
  saveError.value = false
  saveErrorMessage.value = ''
}

const clearSaveSuccess = () => {
  saveSuccess.value = false
}
</script>

<style lang="scss" scoped>
.ccma-vat-liability {
  padding: 0;
  margin: 0;
  background-color: var(--vt-c-white);
  min-height: 100vh;
  position: relative;
}

/* Loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(white, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #e67e22;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  @include typography-body;
  color: #666;
  margin: 0;
}

/* Error and Success notifications */
.save-error-notification,
.save-success-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  max-width: 400px;
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 4px 12px rgba(black, 0.2);
  z-index: $z-notification;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.save-error-notification {
  border-left: 4px solid #dc3545;
}

.save-success-notification {
  border-left: 4px solid #28a745;
}

.error-content,
.success-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  position: relative;
}

.error-icon,
.success-icon {
  @include typography-h4;
  margin-right: 0.75rem;
  margin-top: 0.1rem;
}

.error-details h3,
.success-message p {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 0.25rem 0;
}

.error-details p {
  @include typography-small;
  margin: 0;
  color: #666;
}

.error-close,
.success-close {
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  @include typography-h4;
  background: none;
  border: none;
  cursor: pointer;
  color: #999999;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-close:hover,
.success-close:hover {
  color: #666;
}

.error-actions {
  padding: 0 1rem 1rem 1rem;
  border-top: 1px solid #e9ecef;
  padding-top: 0.75rem;
}

/* Header section */
.vat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid #e9ecef;
  background-color: var(--vt-c-white);
}

.vat-title {
  @include typography-h3;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 0.25rem;
}

.vat-subtitle {
  @include typography-body;
  color: #666;
  margin-bottom: 0;
}

.vat-buttons {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

/* Form container */
.vat-form-container {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

.vat-form-content {
  background-color: var(--vt-c-white);
}

.k-form-fieldset {
  border: none;
  padding: 0;
  margin: 0;
}

.form-section {
  margin-bottom: 2rem;
}

.section-title {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e67e22;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  color: #333;
  margin-bottom: 0.5rem;
  display: block;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;
  height: 40px;
}

.form-input:focus {
  border-color: #e67e22;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(#e67e22, 0.25);
}

.form-input.error {
  border-color: #dc3545;
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

.help-text {
  @include typography-small;
  color: #28a745;
  margin-top: 0.25rem;
}

/* Buttons */
.baseBtn {
  @include typography-body;
  @include font-weight-medium;
  padding: 0.75rem 1.5rem;
  border-radius: $button-radius;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
}


.primaryBtn:disabled {
  background-color: #bdbdbd;
  border-color: #bdbdbd;
  cursor: not-allowed;
}

/* Kendo UI component overrides */
:deep(.k-dropdownlist) {
  width: 100%;
  border-radius: $radius-sm;
  height: 40px;
}

:deep(.k-dropdown .k-dropdown-wrap) {
  border-color: #e0e0e0;
  border-radius: $radius-sm;
  padding: 0.75rem;
}

:deep(.k-dropdown .k-dropdown-wrap:focus) {
  border-color: #e67e22;
  box-shadow: 0 0 0 0.2rem rgba(#e67e22, 0.25);
}

/* Responsive design */
@media (max-width: 768px) {
  .vat-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 1.5rem 1rem 1rem 1rem;
  }

  .vat-buttons {
    justify-content: stretch;
  }

  .vat-buttons .baseBtn {
    flex: 1;
  }

  .vat-form-container {
    padding: 1.5rem 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .save-error-notification,
  .save-success-notification {
    left: 10px;
    right: 10px;
    max-width: none;
  }
}

.mb-1 {
  margin-bottom: 0.25rem;
}
</style>
